import { FC, ReactNode, useState } from 'react';
import {
  DotsSixVertical,
  DotsThreeVertical,
  CopySimple,
  TrashSimple,
  ArrowUp,
  ArrowDown,
  CaretDown,
  ListBullets,
  StackSimple,
  ArrowsLeftRight,
  Clock,
  Barbell,
} from '@phosphor-icons/react';
import { BlockType } from '../../../endpoints/coachSessions';
import styles from './BlockBase.module.scss';

// ─── Block type config ────────────────────────────────────────────────────

export const BLOCK_TYPE_CONFIG: Record<
  BlockType,
  {
    label: string;
    color: string;
    softBg: string; // sfondo tenue per chip soft
    description: string;
    Icon: FC<{ size?: number; color?: string }>;
  }
> = {
  sequential: {
    label: 'Sequenziale',
    color: '#063444',
    softBg: '#063444',
    description: 'Esercizi in fila, uno dopo l\'altro',
    Icon: ({ size, color }) => <ListBullets size={size} color={color} />,
  },
  parallel: {
    label: 'Parallelo',
    color: '#10b981',
    softBg: '#10b981',
    description: 'Tecnico/tattico — esercizi su più campi simultanei',
    Icon: ({ size, color }) => <StackSimple size={size} color={color} />,
  },
  rotation: {
    label: 'Rotazione',
    color: '#f59e0b',
    softBg: '#f59e0b',
    description: 'Stazioni con rotazione di gruppi',
    Icon: ({ size, color }) => <ArrowsLeftRight size={size} color={color} />,
  },
  break: {
    label: 'Pausa',
    color: '#94a3b8',
    softBg: '#94a3b8',
    description: 'Recupero / acqua / pausa attiva',
    Icon: ({ size, color }) => <Clock size={size} color={color} />,
  },
  physical: {
    label: 'Atletico',
    color: '#ef4444',
    softBg: '#ef4444',
    description: 'Forza, velocità, condizionamento atletico',
    Icon: ({ size, color }) => <Barbell size={size} color={color} />,
  },
};

// ─── Props ────────────────────────────────────────────────────────────────

interface BlockBaseProps {
  index: number;
  blockType: BlockType;
  title: string;
  durationMinutes?: number;
  transitionAfterMinutes?: number;
  onTransitionChange: (val: number) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  children: ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────

export const BlockBase: FC<BlockBaseProps> = ({
  index,
  blockType,
  title,
  durationMinutes,
  transitionAfterMinutes,
  onTransitionChange,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  children,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [actionsOpen, setActionsOpen] = useState(false);
  const config = BLOCK_TYPE_CONFIG[blockType];

  return (
    <div
      className={styles.block}
      style={{ '--block-accent': config.color } as React.CSSProperties}
    >
      <div className={styles.blockColorBar} />
      {/* ─── Header ─────────────────────────────────────────── */}
      <div className={styles.blockHeader} onClick={() => setExpanded((e) => !e)}>
        <div className={styles.blockHeaderLeft}>
          <span
            className={styles.dragHandle}
            onClick={(e) => e.stopPropagation()}
          >
            <DotsSixVertical size={16} />
          </span>
          <span className={styles.orderNum}>{index + 1}</span>
          <span
            className={styles.blockTypeIcon}
            style={{ background: `${config.color}1A`, color: config.color }}
            title={config.label}
          >
            <config.Icon size={16} color={config.color} />
          </span>
          <span
            className={styles.blockTypeChip}
            style={{ background: config.color }}
          >
            {config.label}
          </span>
          <span className={styles.blockTitle}>{title || 'Blocco senza nome'}</span>
          {durationMinutes != null && (
            <span className={styles.blockDuration}>{durationMinutes}'</span>
          )}
        </div>
        <div className={styles.blockHeaderRight} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className={styles.actionBtn}
            onClick={onMoveUp}
            title="Sposta su"
          >
            <ArrowUp size={13} />
          </button>
          <button
            type="button"
            className={styles.actionBtn}
            onClick={onMoveDown}
            title="Sposta giù"
          >
            <ArrowDown size={13} />
          </button>
          <button
            type="button"
            className={styles.actionBtn}
            onClick={onDuplicate}
            title="Duplica"
          >
            <CopySimple size={13} />
          </button>
          <button
            type="button"
            className={`${styles.actionBtn} ${styles.danger}`}
            onClick={onDelete}
            title="Elimina"
          >
            <TrashSimple size={13} />
          </button>
          <span
            className={`${styles.expandIcon} ${expanded ? styles.expanded : ''}`}
          >
            <CaretDown size={14} />
          </span>
        </div>
      </div>

      {/* ─── Body ───────────────────────────────────────────── */}
      {expanded && <div className={styles.blockBody}>{children}</div>}

      {/* ─── Transition after ────────────────────────────────── */}
      <div className={styles.transitionRow}>
        <span className={styles.transitionLabel}>Transizione dopo:</span>
        <input
          className={styles.transitionInput}
          type="number"
          min={0}
          max={60}
          value={transitionAfterMinutes ?? 0}
          onChange={(e) => onTransitionChange(Number(e.target.value))}
          onClick={(e) => e.stopPropagation()}
        />
        <span className={styles.transitionLabel}>min</span>
      </div>
    </div>
  );
};
